import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import moment from "moment";

import { chartColors } from "common/constant";
import Backdrop from "Components/Common/Backdrop";

import { useGetApplicationStatusHistoryByApplicationNumberQuery } from "apis/ApplicationStatusTracking";
import { useApplicantStatusQuery } from "apis/ApplicationStatus";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import downloadImageIcon from "assets/images/svg/download-file-icon.svg";
import { useLazyDownloadExcelQuery } from "slices/api";

import zoomIcon from "assets/images/svg/zoom-icon.svg";
import zoomOutIcon from "assets/images/svg/zoom-out-icon.svg";
import panIcon from "assets/images/svg/panning-icon.svg";
import homeIcon from "assets/images/svg/home-icon.svg";

const ApplicationStatusHistoryChart = ({
  applicationId = "",
  isFireSafety = false,
}) => {
  const { data: { data: applicationStatus } = {} } = useApplicantStatusQuery(
    {},
    isFireSafety
  );
  const { data: { data: applicationHistory } = {} } =
    useGetApplicationStatusHistoryByApplicationNumberQuery(
      applicationId,
      isFireSafety
    );
  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();
  const [series, setSeries] = useState([]);
  useEffect(() => {
    if (applicationHistory && applicationStatus) {
      const chartData = applicationStatus.flatMap(({ id, name }, index) => {
        const currentData =
          applicationHistory.filter(
            ({ applicationStatus, buildingInformationStatus }) =>
              (isFireSafety ? buildingInformationStatus : applicationStatus) ===
              id
          ) || [];

        return currentData.map(({ startDateTime, endDateTime }) => ({
          x: name,
          y:
            startDateTime && endDateTime
              ? [
                  convertUTCDateToLocalDate(startDateTime).unix() * 1000,
                  convertUTCDateToLocalDate(endDateTime).unix() * 1000,
                ]
              : [],
          fillColor: chartColors[index],
        }));
      });

      setSeries([{ data: chartData }]);
    }
  }, [applicationHistory, applicationStatus]);

  const downloadStatusHistoryReport = async () => {
    try {
      await downloadExcel({
        endpoint: isFireSafety
          ? `/building-information-status-tracking/${applicationId}/building-information-status-history-excel`
          : `/report/application/${applicationId}/application-status-history-excel`,
        sheetName: isFireSafety
          ? "Building Information Status History"
          : "Application Status History-Indi",
      });
    } catch (error) {}
  };

  const options = {
    chart: {
      height: 450,
      type: "rangeBar",
      toolbar: {
        tools: {
          zoom: false,
          download: false,
          selection: false,
          zoomin: `<img src=${zoomIcon} width=${24}>`,
          zoomout: `<img src=${zoomOutIcon} width=${24}>`,
          pan: `<img src=${panIcon} width=${26}>`,
          reset: `<img src=${homeIcon} width=${21}>`,
          customIcons: [
            {
              icon: `<img src=${downloadImageIcon} width=${15}>`,
              index: 4,
              title: "Download Application Status History Report",
              click: downloadStatusHistoryReport,
            },
          ],
        },
      },
    },
    fill: {
      colors: chartColors,
    },

    plotOptions: {
      bar: {
        horizontal: true,
      },
    },
    legend: {
      position: "top",
    },

    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
      },
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const hoveredData =
          w.globals.initialSeries[seriesIndex].data[dataPointIndex];

        const startDateInSeconds = moment.unix(hoveredData.y[0] / 1000);
        const endDateInSeconds = moment.unix(hoveredData.y[1] / 1000);

        return `<div class="arrow_box"> 
          <span>
          <b>
          From : 
          </b>
          ${startDateInSeconds.format("DD-MM-YYYY hh:mm:ss A")}
          </div>
          <div class="arrow_box">
          <span>
          <b>
          To : 
          </b>
          ${endDateInSeconds.format("DD-MM-YYYY hh:mm:ss A")}
          </span>
          </div>`;
      },
    },
  };

  return (
    <>
      <Backdrop open={isExcelFileDownloading} />
      {!!applicationHistory && (
        <ReactApexChart
          options={options}
          series={series}
          type="rangeBar"
          height={400}
        />
      )}
    </>
  );
};

ApplicationStatusHistoryChart.propTypes = {
  applicationId: PropTypes.number,
  isFireSafety: PropTypes.bool,
};

export default ApplicationStatusHistoryChart;
